// A search box that holds its own draft and commits on a pause.
//
// `?q=` goes to the API, so committing per keystroke would be one request per
// letter — and with results paged server-side, half of them arrive out of
// order. The draft also re-syncs when the committed value changes underneath
// it: a "clear filters" press, or landing on a URL that already carries `?q=`.

import { useEffect, useRef, useState, type ReactNode } from "react";
import styles from "./SearchInput.module.css";

const SEARCH_DEBOUNCE_MS = 300;

export interface SearchInputProps {
  value: string;
  onCommit: (next: string) => void;
  placeholder: string;
  ariaLabel: string;
  className?: string;
}

export function SearchInput({
  value,
  onCommit,
  placeholder,
  ariaLabel,
  className,
}: SearchInputProps): ReactNode {
  const [draft, setDraft] = useState(value);
  // Read through a ref so the debounce effect depends on the draft alone; a
  // dependency on the (usually inline) callback would restart the timer on
  // every parent render and never fire.
  const latest = useRef({ value, onCommit });

  useEffect(() => {
    latest.current = { value, onCommit };
  });

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    const { value: committed, onCommit: commit } = latest.current;
    if (draft === committed) return;
    const timer = setTimeout(() => commit(draft), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft]);

  return (
    <div className={[styles.wrap, className].filter(Boolean).join(" ")}>
      <span className={styles.glyph} aria-hidden="true">
        ⌕
      </span>
      <input
        type="search"
        className={styles.input}
        placeholder={placeholder}
        aria-label={ariaLabel}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
      {draft !== "" && (
        <button
          type="button"
          className={styles.clear}
          aria-label={`Clear ${ariaLabel.toLowerCase()}`}
          onClick={() => setDraft("")}
        >
          ✕
        </button>
      )}
    </div>
  );
}
